import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { getErrorMessage } from '@/shared/api/api-error';

import { decide, getInbox } from './approvals.api';

export const inboxKeys = {
  all: ['approvals-inbox'] as const,
  list: (params: { status: string; type?: string }) => [...inboxKeys.all, params] as const,
};

/** The areas whose data an approval changes (a refund also closes its complaint) — kept literal to avoid importing those features. */
const AFFECTED_KEYS = [
  ['pricing'],
  ['refunds'],
  ['providers'],
  ['orders'],
  ['complaints'],
  ['dashboard'],
] as const;

export function useInbox(params: { status: string; type?: string }) {
  return useQuery({
    queryKey: inboxKeys.list(params),
    queryFn: ({ signal }) => getInbox(params, signal),
    placeholderData: (previous) => previous,
  });
}

/**
 * Everything waiting that the viewer may see, for badges (the sidebar, a tab).
 * Shares the inbox's cache and refreshes every minute. Pass false without
 * approvals.read, so nothing is requested.
 */
export function usePendingApprovals(enabled: boolean) {
  return useQuery({
    queryKey: inboxKeys.list({ status: 'Pending' }),
    queryFn: ({ signal }) => getInbox({ status: 'Pending' }, signal),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useDecide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      type: string;
      approvalId: number;
      decision: 'approve' | 'reject' | 'cancel';
      reason: string | null;
    }) => decide(input.type, input.approvalId, input.decision, input.reason),
    onSuccess: (_result, input) => {
      toast.success(
        input.decision === 'approve'
          ? 'Approved.'
          : input.decision === 'reject'
            ? 'Rejected.'
            : 'Request withdrawn.',
      );
      void queryClient.invalidateQueries({ queryKey: inboxKeys.all });
      for (const key of AFFECTED_KEYS) void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Could not decide the request.')),
  });
}
